<script setup lang="ts">
import { ButtonColor, ButtonSize, ButtonVariant } from '@/hooks/useButtonClasses';

import Button from '@/components/button/Button.vue';
import Dialog from '@/components/dialog/Dialog.vue';
import FormTextInput from '@/components/formTextInput/FormTextInput.vue';

import MobileAndroid from '@/icons/line/mobile-android.svg';
import Envelope from '@/icons/line/envelope.svg';

export type CreateAccountTwoFactorChannelProps = {
  codeSentToChannel?: App.Auth.Data.TwoFactorChannelData;
};

const props = defineProps<CreateAccountTwoFactorChannelProps>();

const { show, close } = useDialog();

const sendCode = useForm({
  method: 'POST',
  fields: {}
});

const verifyCode = useForm({
  method: 'POST',
  fields: {
    code: ''
  }
});

const codeLength = 6;

const intervalTimer = ref<NodeJS.Timeout | null>(null);
const selectedChannel = ref(props.codeSentToChannel || null);
const timeRemaining = ref(0);
const codeJustSent = ref(false);
const codeSentTimer = ref<NodeJS.Timeout | null>(null);

const codeEntered = computed(() => verifyCode.fields.code.length === codeLength);

const formattedTimeRemaining = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60);
  const seconds = timeRemaining.value % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
});

function calculateTimeRemaining(): number {
  if (!selectedChannel.value?.latestCodeDeliveredAt) {
    return 0;
  }

  const sentAt = new Date(selectedChannel.value.latestCodeDeliveredAt);
  const expiresAt = new Date(sentAt.getTime() + 30 * 1000); // 30 seconds in milliseconds
  const now = new Date();

  return Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 1000));
}

function startCountdown() {
  timeRemaining.value = calculateTimeRemaining();

  if (timeRemaining.value <= 0) {
    return;
  }

  intervalTimer.value = setInterval(() => {
    timeRemaining.value = calculateTimeRemaining();

    if (timeRemaining.value <= 0) {
      clearInterval(intervalTimer.value!);
    }
  }, 1000);
}

function stopCountdown() {
  if (intervalTimer.value) {
    clearInterval(intervalTimer.value);
    intervalTimer.value = null;
  }
}

function stopCodeSentTimer() {
  if (codeSentTimer.value) {
    clearTimeout(codeSentTimer.value);
    codeSentTimer.value = null;
  }
}

onMounted(() => {
  if (selectedChannel.value?.latestCodeDeliveredAt) {
    startCountdown();
  }
});

onUnmounted(() => {
  stopCountdown();
  stopCodeSentTimer();
});

function handleSubmit() {
  verifyCode.submitWith({
    url: route('account.two-factor.challenge.store', {
      channel: selectedChannel.value!.id
    })
  });
}

function handleResendCode() {
  if (selectedChannel?.value?.id) {
    sendCode.submitWith({
      url: route('account.two-factor.deliver-code.store', {
        channel: selectedChannel.value.id
      }),
      hooks: {
        success() {
          if (selectedChannel.value) {
            selectedChannel.value.latestCodeDeliveredAt = new Date().toISOString();
            stopCountdown();
            startCountdown();

            codeJustSent.value = true;
            stopCodeSentTimer();
            codeSentTimer.value = setTimeout(() => {
              codeJustSent.value = false;
            }, 5000);
          }
        }
      }
    });
  }
}
</script>

<template>
  <Dialog
    title="Enter your verification code"
    confirmButtonLabel="Add channel"
    :confirmButtonDisabled="!codeEntered"
    :isOpen="show"
    @onClose="close"
    @onCancel="close"
    @onConfirm="handleSubmit"
  >
    <div class="w-full space-y-6">
      <div class="flex flex-col items-center space-y-4">
        <div class="flex h-21 w-21 items-center justify-center rounded-full bg-slate-100">
          <Envelope
            v-if="selectedChannel?.channelType === 'email'"
            class="h-8 w-8 text-slate-500"
          />
          <MobileAndroid v-else class="h-8 w-8 text-slate-500" />
        </div>
        <div class="w-full space-y-2 text-sm text-slate-900">
          <p class="leading-5">
            We just sent you a
            {{ selectedChannel?.channelType === 'sms' ? 'text message' : 'email' }} with a
            {{ codeLength }} digit verification code to:
          </p>
          <p class="text-lg font-semibold">{{ selectedChannel?.formattedDestination }}</p>
        </div>
      </div>
      <div class="space-y-2">
        <FormTextInput
          label="Enter Verification Code"
          placeholder="••••••"
          name="code"
          type="text"
          autofocus
          :value="verifyCode.fields.code"
          :error="verifyCode.errors.code"
          @update:model-value="(value) => (verifyCode.fields.code = value as string)"
        />
        <div class="flex justify-end">
          <Transition
            enterActiveClass="transition-opacity duration-300"
            leaveActiveClass="transition-opacity duration-300"
            enterFromClass="opacity-0"
            enterToClass="opacity-100"
            leaveFromClass="opacity-100"
            leaveToClass="opacity-0"
            mode="out-in"
          >
            <div v-if="codeJustSent" class="text-sm font-medium leading-6 text-green-600">
              Code sent!
            </div>
            <Button
              v-else
              :variant="ButtonVariant.invisible"
              :size="ButtonSize.xs"
              :color="ButtonColor.secondary"
              :disabled="timeRemaining > 0"
              @click="handleResendCode"
              >{{
                timeRemaining > 0 ? `Resend code in ${formattedTimeRemaining}` : 'Resend code'
              }}</Button
            >
          </Transition>
        </div>
      </div>
    </div>
  </Dialog>
</template>
